import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Search, Send, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const isDispatcher = user?.role === "dispatcher";
  const isAdmin = user?.role === "admin";
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [edit, setEdit] = useState(null);      // {name,email,phone} or null
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/customers/${id}`);
    setDetail(data);
    setEdit(null);
  };

  const startEdit = () => setEdit({
    name: detail.customer.name || "", email: detail.customer.email || "", phone: detail.customer.phone || "",
  });

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/customers/${detail.customer.id}`, edit);
      setDetail({ ...detail, customer: data });
      setEdit(null);
      refresh();
      toast.success("Customer profile updated");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const deleteCustomer = async () => {
    setBusy(true);
    try {
      await api.delete(`/customers/${detail.customer.id}`);
      setConfirmDel(false);
      setDetail(null);
      refresh();
      toast.success("Customer and all associated data deleted");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/customers/${detail.customer.id}/messages`, { text: reply.trim() });
      setDetail({ ...detail, messages: [...detail.messages, data] });
      setReply("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = customers.filter((c) =>
    (c.name + c.email).toLowerCase().includes(query.toLowerCase()));

  return (
    <div data-testid="ops-customers-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Accounts</p>
          <h1 className="page-title mt-1.5">Customers</h1>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="customers-search-input" className="pl-9 w-64 rounded-full" placeholder="Search customers..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>

      <div className="rounded-2xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-secondary/50">
              {["Customer", "Contact", "Jobs", ...(isDispatcher ? [] : ["Lifetime Value"]), "Since"].map((h) => (
                <th key={h} className="text-left px-5 py-3 th-cc font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {shown.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => openDetail(c.id)} className="cursor-pointer hover:bg-secondary/40 transition-colors duration-150">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--cc-accent)]/15 text-[var(--cc-accent)] flex items-center justify-center font-heading font-bold text-xs shrink-0">{c.name[0]}</div>
                    <span className="font-semibold">{c.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-muted-foreground">{c.email}<br /><span className="text-xs">{c.phone}</span></td>
                <td className="px-6 py-4 font-mono-cc">{c.missions_count}</td>
                {!isDispatcher && <td className="px-6 py-4 font-semibold">{fmtMoney(c.invoices_total)}</td>}
                <td className="px-6 py-4 text-muted-foreground text-xs">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">No customers found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="customer-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between gap-3 pr-6">
                  <SheetTitle className="font-heading">{detail.customer.name}</SheetTitle>
                  {isAdmin && !edit && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button data-testid="customer-edit-button" size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={startEdit}>
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                      </Button>
                      <Button data-testid="customer-delete-button" size="sm" variant="outline"
                        className="rounded-full h-8 text-xs text-red-500 border-red-500/30 hover:bg-red-500/10 hover:text-red-500"
                        onClick={() => setConfirmDel(true)}>
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    </div>
                  )}
                </div>
              </SheetHeader>
              <div className="space-y-6 mt-6 text-sm">
                {edit ? (
                  <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="customer-edit-form">
                    <div className="space-y-1"><Label className="text-xs font-bold">Name</Label>
                      <Input data-testid="customer-edit-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs font-bold">Email</Label>
                      <Input data-testid="customer-edit-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs font-bold">Phone</Label>
                      <Input data-testid="customer-edit-phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
                    <div className="flex gap-2 pt-1">
                      <Button data-testid="customer-edit-save" size="sm" disabled={busy} onClick={saveEdit}
                        className="rounded-full bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save</>}
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setEdit(null)}><X className="w-3.5 h-3.5 mr-1.5" /> Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">{detail.customer.email} · {detail.customer.phone || "No phone"}</p>
                )}
                <div>
                  <p className="overline-cc mb-2">Missions ({detail.missions.length})</p>
                  <div className="space-y-2">
                    {detail.missions.map((m) => (
                      <div key={m.id} className="rounded-lg border p-3 flex items-center justify-between gap-3">
                        <div>
                          <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number}</p>
                          <p className="font-semibold">{serviceName(m.service_type)}</p>
                        </div>
                        <StatusBadge status={m.status} />
                      </div>
                    ))}
                    {detail.missions.length === 0 && <p className="text-muted-foreground text-xs">No missions yet.</p>}
                  </div>
                </div>
                {!isDispatcher && (
                <div>
                  <p className="overline-cc mb-2">Invoices ({detail.invoices.length})</p>
                  <div className="space-y-2">
                    {detail.invoices.map((inv) => (
                      <div key={inv.id} className="rounded-lg border p-3 flex items-center justify-between gap-3">
                        <p className="font-mono-cc text-xs">{inv.invoice_number}</p>
                        <p className="font-semibold">{fmtMoney(inv.total)}</p>
                        <StatusBadge status={inv.status} />
                      </div>
                    ))}
                    {detail.invoices.length === 0 && <p className="text-muted-foreground text-xs">No invoices yet.</p>}
                  </div>
                </div>
                )}
                <div>
                  <p className="overline-cc mb-2">Support Thread</p>
                  <div className="rounded-lg border p-3 space-y-3 max-h-56 overflow-y-auto">
                    {detail.messages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed ${m.sender === "staff" ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)]" : "bg-secondary border"}`}>{m.text}</div>
                      </div>
                    ))}
                    {detail.messages.length === 0 && <p className="text-muted-foreground text-xs">No messages.</p>}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Input data-testid="customer-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply as CastleCorp Ops..." />
                    <Button data-testid="customer-reply-send" size="icon" onClick={sendReply} className="shrink-0 bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90"><Send className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid="customer-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold text-foreground">{detail?.customer?.name}</span> and all associated data —
              jobs, invoices, quotes, leads, and support messages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="customer-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="customer-delete-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); deleteCustomer(); }}
              className="bg-red-500 hover:bg-red-600 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
